import { useState, useCallback } from 'react'
import { message } from 'antd'
import { RecognitionRecord } from '../types/contextInterface'
import { getTabName } from '../lib/columnConfigs'

interface UseTableEditingProps {
  onSaveRecord: (
    record: RecognitionRecord,
    rowIndex: number,
    colIndex?: number
  ) => Promise<void>
  onUpdateFixedData?: (record: RecognitionRecord) => void
  onReportAction: (params: any) => void
  generateTrackingParams: (record: RecognitionRecord, colIndex?: number) => any
  onDeleteRecord?: (record: RecognitionRecord) => Promise<void>
  onAgainGenerate?: (
    record: RecognitionRecord,
    fieldName: string,
    rowIndex: number
  ) => Promise<void>
}

/**
 * 表格编辑相关的自定义Hook
 * 处理编辑开始、数据比较和编辑保存的完整流程
 */
export const useTableEditing = ({
  onSaveRecord,
  onUpdateFixedData,
  onReportAction,
  generateTrackingParams,
  onAgainGenerate
}: UseTableEditingProps) => {
  // 用于存储编辑前的记录数据
  const [editingRecord, setEditingRecord] = useState<{
    record?: RecognitionRecord
    colIndex?: number
  }>({})

  /**
   * 处理编辑开始事件
   * @param record - 记录数据
   * @param rowIndex - 行索引（第几行，从0开始）
   * @param colIndex - 列索引（第几列，从0开始）
   */
  const handleEditStart = useCallback(
    (record: RecognitionRecord, rowIndex: number, colIndex: number) => {
      if (record.id === -999 || record.id === -1) {
        return
      }

      // 保存编辑前的记录数据，用于后续比较
      setEditingRecord({ record: { ...record }, colIndex })

      // 点击编辑内容 - 使用编辑前的数据
      onReportAction({
        actionType: 'editContent',
        rowIndex,
        colIndex,
        ...generateTrackingParams(record, colIndex)
      })
    },
    [onReportAction, generateTrackingParams]
  )

  /**
   * 比较两个记录是否有差异
   * @param oldRecord - 编辑前的记录
   * @param newRecord - 编辑后的记录
   * @param colIndex - 列索引
   * @returns 是否有差异
   */
  const hasRecordChanged = useCallback(
    (
      oldRecord?: RecognitionRecord,
      newRecord?: RecognitionRecord,
      colIndex?: number
    ) => {
      if (!oldRecord || !newRecord || colIndex === undefined) return false

      // 获取列的数据索引
      const columnInfo = getTabName(colIndex)
      if (!columnInfo || !columnInfo.dataIndex) return false

      const dataIndex = columnInfo.dataIndex

      // 特殊处理数组类型的字段
      if (dataIndex === 'potentialHazardList' || dataIndex === 'standardList') {
        const oldFieldValue = oldRecord[dataIndex]
        const newFieldValue = newRecord[dataIndex]
        const oldValue = Array.isArray(oldFieldValue)
          ? oldFieldValue.join('\n')
          : String(oldFieldValue || '')

        const newValue = Array.isArray(newFieldValue)
          ? newFieldValue.join('\n')
          : String(newFieldValue || '')

        return oldValue !== newValue
      }

      // 处理普通字段
      const oldValue = String(oldRecord[dataIndex] || '')
      const newValue = String(newRecord[dataIndex] || '')

      return oldValue !== newValue
    },
    []
  )

  /**
   * 处理记录保存逻辑
   * @param record - 记录数据
   * @param rowIndex - 行索引（第几行，从0开始）
   * @param colIndex - 列索引（可选，第几列，从0开始）
   */
  const handleRecordSave = useCallback(
    async (record: RecognitionRecord, rowIndex: number, colIndex?: number) => {
      try {
        if (rowIndex === 0 && onUpdateFixedData) {
          // 更新固定数据状态
          onUpdateFixedData(record)
        }

        // 获取编辑前的记录和列索引
        const { record: oldRecord, colIndex: editColIndex } = editingRecord

        // 使用实际传入的列索引或保存的列索引
        const actualColIndex = colIndex !== undefined ? colIndex : editColIndex

        // 检查数据是否有变化
        const isChanged = hasRecordChanged(oldRecord, record, actualColIndex)

        // 只有在数据有变化时才保存到服务器
        if (isChanged) {
          await onSaveRecord(record, rowIndex, actualColIndex)

          let content = ''
          let fieldName = ''
          if (typeof actualColIndex === 'number') {
            const columnInfo = getTabName(actualColIndex)
            if (!columnInfo) return
            const dataIndex = columnInfo.dataIndex
            fieldName = dataIndex

            // 处理数组类型的字段，将其转换为逗号分隔的字符串
            if (
              dataIndex === 'potentialHazardList' ||
              dataIndex === 'standardList'
            ) {
              const fieldValue = record[dataIndex]
              content = Array.isArray(fieldValue)
                ? fieldValue.join('\n')
                : String(fieldValue || '')
            } else {
              content = record[dataIndex] || ''
            }
          }
          if (!oldRecord) {
            return
          }

          // 编辑结束后上报埋点 - 使用编辑后的数据
          onReportAction({
            actionType: 'editContent',
            rowIndex,
            colIndex: actualColIndex,
            isChanged: true,
            content: content,
            ...generateTrackingParams(oldRecord, actualColIndex)
          })

          // 如果修改了隐患相关字段，自动调用重新识别
          if (onAgainGenerate && fieldName === 'potentialHazardList') {
            try {
              await onAgainGenerate(record, fieldName, rowIndex)
            } catch (error) {
              console.error('自动重新识别失败:', error)
            }
          }
        } else {
          console.log('数据未发生变化，跳过保存和埋点上报')
        }

        // 清空编辑记录
        setEditingRecord({})
      } catch (error) {
        console.error('保存记录失败:', error)
        message.error('保存失败，请重试')
      }
    },
    [
      onSaveRecord,
      onUpdateFixedData,
      editingRecord,
      hasRecordChanged,
      onReportAction,
      generateTrackingParams
    ]
  )

  return {
    handleEditStart,
    handleRecordSave,
    hasRecordChanged,
    editingRecord
  }
}

export default useTableEditing
